import React from 'react';
import { darken, lighten } from '@mui/system';

const DEFAULT_FONT_STACK = 'Roboto, Helvetica, Arial, sans-serif';

const fontFamilyFromName = (name) => `${JSON.stringify(name)}, ${DEFAULT_FONT_STACK}`;

const normalizeFontConfig = (fontConfig) => {
  if (!fontConfig) return {};

  // New API shape can be string URLs; RSVP shape can be objects with name/font.
  if (typeof fontConfig.regular === 'string' || typeof fontConfig.bold === 'string') {
    return {
      regular: fontConfig.regular ? { name: 'Frontend Regular', font: fontConfig.regular } : null,
      bold: fontConfig.bold ? { name: 'Frontend Bold', font: fontConfig.bold } : null,
    };
  }

  return {
    regular: fontConfig.regular || null,
    bold: fontConfig.bold || null,
  };
};

export default function GlobalStyles({ colors, customCss, fonts }) {
  const primaryColor = colors?.primary || colors?.primaryColor || '#1976d2';
  const backgroundColor = colors?.background || colors?.backgroundColor || '#ffffff';
  const primaryContrastColor = colors?.primaryContrast || colors?.primaryContrastColor || '#ffffff';

  const safeCustomCss = customCss || '';
  const normalizedFonts = normalizeFontConfig(fonts);
  const regularFontName = normalizedFonts?.regular?.name;
  const regularFontUrl = normalizedFonts?.regular?.font;
  const boldFontName = normalizedFonts?.bold?.name;
  const boldFontUrl = normalizedFonts?.bold?.font;
  const hasCustomCssFontControl = /@font-face|font-family/i.test(safeCustomCss);

  const apiFontCss = hasCustomCssFontControl ? '' : `
    ${regularFontName && regularFontUrl ? `
      @font-face {
        font-family: ${JSON.stringify(regularFontName)};
        src: url('${regularFontUrl}') format('woff2');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }
    ` : ''}
    ${boldFontName && boldFontUrl ? `
      @font-face {
        font-family: ${JSON.stringify(boldFontName)};
        src: url('${boldFontUrl}') format('woff2');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
      }
    ` : ''}
    ${regularFontName ? `
      html, body, #root {
        font-family: ${fontFamilyFromName(regularFontName)} !important;
      }
    ` : ''}
    ${regularFontName ? `
      * {
        font-family: ${fontFamilyFromName(regularFontName)} !important;
      }
    ` : ''}
  `;

  return (
    <style>
      {safeCustomCss}
      {`
        ${apiFontCss}
        html {
          --mui-palette-primary-main: ${primaryColor} !important;
          --mui-palette-primary-contrastText: ${primaryContrastColor} !important;
          --mui-palette-primary-mainChannel: ${lighten(primaryColor, 0.9)} !important;
          --mui-palette-primary-dark: ${darken(primaryColor, 0.2)} !important;
          --template-palette-background-default: ${backgroundColor};
        }
        body {
          background-color: ${backgroundColor};
        }
      `}
    </style>
  );
}
